import React, { useMemo, useState } from "react";

function RevenueChangeLog({ table_data }) {
  const [openSection, setOpenSection] = useState("this");
  const [selectedUser, setSelectedUser] = useState("firm");

  const changeLog = table_data || [];

  // Currency settings
  const userLogging = JSON.parse(localStorage.getItem("user") || "{}");
  const currencies = JSON.parse(
    localStorage.getItem("currency_rates") || "[]"
  );

  const defaultCurrencyId = Number(
    userLogging?.default_currency_id || 1
  );

  const selectedCurrency = currencies.find(
    (currency) => Number(currency.value) === defaultCurrencyId
  );

  const symbol =
    selectedCurrency?.symbol ||
    (defaultCurrencyId === 1 ? "₹" : "");

  const currencyType = defaultCurrencyId === 1 ? "en-IN" : "en-US";

  // Remove duplicate adjustment records using adjustment_id.
  // Requires a.id AS adjustment_id in the SQL SELECT.
  const uniqueChangeLog = useMemo(() => {
    const unique = new Map();

    changeLog.forEach((item, index) => {
      const key =
        item.adjustment_id != null
          ? String(item.adjustment_id)
          : `fallback-${index}`;

      if (!unique.has(key)) {
        unique.set(key, item);
      }
    });

    return [...unique.values()];
  }, [changeLog]);

  // Employee dropdown logic unchanged:
  // Priority: BD -> RM -> Assign.
  // Sort designation ID ascending within each priority.
  const employeeList = useMemo(() => {
    const employees = new Map();

    uniqueChangeLog.forEach((item) => {
      [
        {
          name: item.bd_by,
          designation: item.bd_by_designation,
          priority: 1,
        },
        {
          name: item.role_manager,
          designation: item.role_manager_designation,
          priority: 2,
        },
        {
          name: item.assign_to,
          designation: item.assign_to_designation,
          priority: 3,
        },
      ].forEach((emp) => {
        if (!emp.name?.trim()) return;

        const existing = employees.get(emp.name);

        if (
          !existing ||
          emp.priority < existing.priority ||
          (emp.priority === existing.priority &&
            Number(emp.designation ?? 999) <
              Number(existing.designation ?? 999))
        ) {
          employees.set(emp.name, emp);
        }
      });
    });

    return [...employees.values()].sort((a, b) => {
      if (a.priority !== b.priority) {
        return a.priority - b.priority;
      }

      return (
        Number(a.designation ?? 999) -
          Number(b.designation ?? 999) ||
        a.name.localeCompare(b.name)
      );
    });
  }, [uniqueChangeLog]);

  // Filter records matching the employee in RM, Assign, or BD.
  const filteredData = useMemo(() => {
    if (selectedUser === "firm") {
      return uniqueChangeLog;
    }

    return uniqueChangeLog.filter(
      (item) =>
        item.role_manager === selectedUser ||
        item.assign_to === selectedUser ||
        item.bd_by === selectedUser
    );
  }, [uniqueChangeLog, selectedUser]);

  // Split filtered records by week.
  const thisWeekData = useMemo(
    () => filteredData.filter(
      (item) => item.week_category === "This Week"
    ),
    [filteredData]
  );

  const prevWeekData = useMemo(
    () => filteredData.filter(
      (item) => item.week_category === "Previous Week"
    ),
    [filteredData]
  );

  // Calculate totals using filtered records.
  const getTotal = (arr) =>
    arr.reduce((sum, item) => sum + Number(item.amount || 0), 0);

  const thisWeekTotal = getTotal(thisWeekData);
  const prevWeekTotal = getTotal(prevWeekData);

  // Weekly growth.
  const growth =
    prevWeekTotal > 0
      ? (
          ((thisWeekTotal - prevWeekTotal) / prevWeekTotal) *
          100
        ).toFixed(2)
      : thisWeekTotal > 0
        ? "100.00"
        : "0.00";

  const toggle = (section) => {
    setOpenSection((current) =>
      current === section ? null : section
    );
  };

  const formatCurrency = (amount) =>
    `${symbol} ${Number(amount || 0).toLocaleString(currencyType, {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    })}`;

  // Table renderer.
  const renderTable = (data) => {
    if (data.length === 0) {
      return <p className="text-muted">No data available</p>;
    }

    return (
      <div className="table-responsive">
        <table className="table table-sm table-hover">
          <thead>
            <tr>
              <th>RM</th>
              <th>Assign</th>
              <th>Client</th>
              <th>Role</th>
              <th>Sector</th>
              <th>Retainer</th>
              <th>Amt</th>
              <th>Date</th>
              <th>Status</th>
              <th>Comments</th>
              <th>BD</th>
            </tr>
          </thead>

          <tbody>
            {data.map((item, index) => (
              <tr
                key={
                  item.adjustment_id != null
                    ? item.adjustment_id
                    : `row-${index}`
                }
              >
                <td>{item.role_manager}</td>
                <td>{item.assign_to}</td>
                <td>{item.client}</td>

                <td>
                  {item.role}
                  {item.financial_year && (
                    <span
                      style={{
                        color: "red",
                        marginLeft: "5px",
                        fontSize: "11px",
                      }}
                    >
                      ({item.financial_year})
                    </span>
                  )}
                </td>

                <td>{item.sector}</td>
                <td>{item.retainer}</td>

                <td
                  style={{
                    textAlign: "right",
                    whiteSpace: "nowrap",
                  }}
                  className={
                    Number(item.amount) < 0
                      ? "text-danger"
                      : "text-success"
                  }
                >
                  {Number(item.amount || 0).toLocaleString("en-IN", {
                    maximumFractionDigits: 0,
                  })}
                </td>

                <td>{item.change_date}</td>
                <td>{item.status}</td>
                <td>{item.comments}</td>
                <td>{item.bd_by}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    );
  };

  return (
    <>
      <style>
        {`
          .log-card {
            border: 1px solid #eee;
            border-radius: 10px;
            margin-bottom: 10px;
            padding: 10px;
            cursor: pointer;
          }

          .log-header {
            display: flex;
            justify-content: space-between;
            align-items: center;
          }

          .positive {
            color: green;
          }

          .negative {
            color: red;
          }

          .arrow {
            font-size: 14px;
            margin-left: 5px;
          }

          .revenue-filter {
            width: 260px;
            max-width: 100%;
          }
        `}
      </style>

      {/* Employee filter */}
      <div className="d-flex flex-wrap justify-content-between align-items-center gap-2 mb-3">
        <label
          htmlFor="revenueUserFilter"
          className="fw-semibold mb-0"
        >
          Filter by Employee
        </label>

        <select
          id="revenueUserFilter"
          className="form-select form-select-sm revenue-filter"
          value={selectedUser}
          onChange={(e) => setSelectedUser(e.target.value)}
        >
          <option value="firm">Firm — All Employees</option>

          {employeeList.map((emp) => (
            <option key={emp.name} value={emp.name}>
              {emp.name}
            </option>
          ))}
        </select>
      </div>

      {/* This Week */}
      <div className="log-card" onClick={() => toggle("this")}>
        <div className="log-header">
          <h6 className="mb-0">This Week</h6>
          <strong>{formatCurrency(thisWeekTotal)}</strong>
        </div>

        {openSection === "this" && (
          <div className="mt-2">
            {renderTable(thisWeekData)}
          </div>
        )}
      </div>

      {/* Previous Week */}
      <div className="log-card" onClick={() => toggle("prev")}>
        <div className="log-header">
          <h6 className="mb-0">Previous Week</h6>
          <strong>{formatCurrency(prevWeekTotal)}</strong>
        </div>

        {openSection === "prev" && (
          <div className="mt-2">
            {renderTable(prevWeekData)}
          </div>
        )}
      </div>

      {/* Growth summary */}
      <div className="mt-3">
        <h6>
          Weekly Growth:{" "}
          <span
            className={
              Number(growth) >= 0 ? "positive" : "negative"
            }
          >
            {growth}%{" "}
            <span className="arrow">
              {Number(growth) >= 0 ? "▲" : "▼"}
            </span>
          </span>
        </h6>
      </div>
    </>
  );
}

export default RevenueChangeLog;